import Column from '@app/components/layout/Column'
import { LocalitySelector } from '@app/components/blocks/smartInputs/LocalitySelector'
import { SearchSelectOption } from '@app/components/core/input/searchSelect/SearchSelect'
import { useTranslations } from '@i18n/useLanguage'
import { useCallback, useEffect, useState } from 'react'
import { useClient } from '@based/sdk/react'
import { useToast } from '@app/util/hooks/useToast'
import { Initiative } from '@schema/initiative'
import { useEditContainerClose } from '../shared/useEditContainerClose'
import { FieldEditActions } from '../shared/FieldEditActions'

export type LocalityFieldEditProps = {
  initiativeId: number
  initiative: Partial<Initiative>
}

export const LocalityFieldEdit = ({
  initiative,
  initiativeId,
}: LocalityFieldEditProps) => {
  const close = useEditContainerClose()
  if (!close) {
    throw new Error('LocalityFieldEdit requires an EditContainer ancestor')
  }
  const client = useClient()
  const { showFailToast, showSuccessToast } = useToast()
  const t = useTranslations().app.common

  const regionId = initiative.location?.address?.region?.id

  const [locality, setLocality] = useState<SearchSelectOption>({
    key: initiative.location?.address?.locality?.id ?? 0,
    value: initiative.location?.address?.locality?.name ?? '',
  })

  useEffect(() => {
    setLocality({
      key: initiative.location?.address?.locality?.id ?? 0,
      value: initiative.location?.address?.locality?.name ?? '',
    })
  }, [
    initiative.location?.address?.locality?.id,
    initiative.location?.address?.locality?.name,
  ])

  const save = useCallback(() => {
    void client
      .call('edit-initiative', {
        id: initiativeId,
        value: {
          location: {
            address: {
              region: regionId ? { id: regionId } : undefined,
              locality: locality.key ? { id: locality.key } : undefined,
              country: undefined,
              municipality: undefined,
              street: null,
              postalCode: null,
            },
            geo: undefined,
          },
        },
      })
      .then((success) => {
        if (success) {
          showSuccessToast(t.toastSuccess)
          close()
        } else {
          showFailToast()
        }
      })
      .catch(() => showFailToast())
  }, [
    client,
    initiativeId,
    locality.key,
    regionId,
    close,
    showFailToast,
    showSuccessToast,
    t.toastSuccess,
  ])

  return (
    <Column gap="1rem" alignItems="stretch">
      <LocalitySelector
        locality={locality}
        setLocality={setLocality}
        filterByRegionId={regionId ?? undefined}
      />
      <FieldEditActions onCancel={close} onSave={save} />
    </Column>
  )
}
